import React, { useState, useRef, useEffect } from 'react'
import { useCollectionImages } from '../hooks/useCollectionImages'
import { useContent } from '../contexts/ContentContext'

interface CollectionGalleryProps {
  collection: 'boys' | 'girls'
}

const CollectionGallery: React.FC<CollectionGalleryProps> = ({ collection }) => {
  const { images, isLoading, error } = useCollectionImages(collection)
  const { content } = useContent()
  const [selectedImage, setSelectedImage] = useState<any>(null)
  const [zoom, setZoom] = useState(1)
  const [inputValue, setInputValue] = useState("100")
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [lastTouchDistance, setLastTouchDistance] = useState<number | null>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const collectionName = collection === 'boys' ? 'Niños' : 'Niñas'
  const collectionData = collection === 'boys' ? content.collections?.boys : content.collections?.girls

  // Sincronizar input con zoom
  useEffect(() => {
    setInputValue(Math.round(zoom * 100).toString())
  }, [zoom])

  // Funciones para manejar zoom y arrastre
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.01, 3))
  }

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.01, 0.5))
  }

  const handleManualZoom = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    const numValue = parseFloat(value)
    if (!isNaN(numValue) && numValue >= 50 && numValue <= 300) {
      setZoom(numValue / 100)
    }
  }

  const handleInputBlur = () => {
    const numValue = parseFloat(inputValue)
    if (isNaN(numValue) || numValue < 50 || numValue > 300) {
      setInputValue(Math.round(zoom * 100).toString())
    }
  }

  const resetZoom = () => {
    setZoom(1)
    setPosition({ x: 0, y: 0 })
    setInputValue("100")
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true)
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // Soporte para gestos táctiles (Pinch to Zoom)
  const getDistance = (touch1: React.Touch, touch2: React.Touch) => {
    return Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const dist = getDistance(e.touches[0], e.touches[1])
      setLastTouchDistance(dist)
    } else if (e.touches.length === 1 && zoom > 1) {
      setIsDragging(true)
      setDragStart({ x: e.touches[0].clientX - position.x, y: e.touches[0].clientY - position.y })
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastTouchDistance) {
      const dist = getDistance(e.touches[0], e.touches[1])
      const delta = dist - lastTouchDistance
      
      // Sensibilidad del pinch zoom
      const zoomChange = delta * 0.005
      setZoom(prev => Math.min(Math.max(prev + zoomChange, 0.5), 3))
      setLastTouchDistance(dist)
    } else if (e.touches.length === 1 && isDragging && zoom > 1) {
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      })
    }
  }

  const handleTouchEnd = () => {
    setIsDragging(false)
    setLastTouchDistance(null)
  }

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    if (e.deltaY < 0) {
      setZoom(prev => Math.min(prev + 0.05, 3)) // Scroll más rápido que botones
    } else {
      setZoom(prev => Math.max(prev - 0.05, 0.5))
    }
  }

  // Resetear zoom cuando se cierra el modal
  useEffect(() => {
    if (!selectedImage) {
      setZoom(1)
      setPosition({ x: 0, y: 0 })
    }
  }, [selectedImage])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando colección...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Error al cargar la colección</p>
        </div>
      </div>
    )
  }

  const activeImages = images.filter(img => img.is_active).sort((a, b) => a.order_index - b.order_index)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container-max py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-800">
                Colección {collectionName}
              </h1>
              <p className="text-xl text-gray-600 mt-2">
                {collectionData?.description || `Descubre nuestra colección de ropa para ${collectionName.toLowerCase()}`}
              </p>
            </div>
            <button
              onClick={() => window.history.back()}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg transition-colors"
            >
              ← Volver
            </button>
          </div>
        </div>
      </div>

      {/* Gallery */}
      <div className="container-max py-12">
        {activeImages.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">
              {collection === 'boys' ? '👦' : '👧'}
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-2">
              Próximamente
            </h3>
            <p className="text-gray-600">
              Estamos preparando nuevas piezas para esta colección
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <p className="text-gray-600">
                {activeImages.length} {activeImages.length === 1 ? 'pieza' : 'piezas'} en esta colección
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {activeImages.map((image) => (
                <div
                  key={image.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="overflow-hidden">
                    <img
                      src={image.full_image_url || image.image_url}
                      alt={image.alt_text}
                      className="w-full aspect-[3/4] object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.src = 'https://via.placeholder.com/300x400/CCCCCC/666666?text=Imagen+no+disponible'
                      }}
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-800 mb-2">
                      {image.alt_text}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal mejorado para imagen ampliada */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl max-h-full overflow-hidden shadow-2xl">
            {/* Header del modal */}
            <div className="flex justify-between items-center p-4 border-b bg-gray-50">
              <h3 className="text-xl font-semibold text-gray-800">
                {selectedImage.alt_text}
              </h3>
              <div className="flex items-center space-x-2">
                {/* Controles de zoom */}
                <div className="flex items-center space-x-1 bg-white rounded-lg border px-2 py-1">
                  <button
                    onClick={handleZoomOut}
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Alejar"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <div className="flex items-center">
                    <input
                      type="text"
                      value={inputValue}
                      onChange={handleManualZoom}
                      onBlur={handleInputBlur}
                      className="w-12 text-center text-sm text-gray-600 border-none focus:ring-0 p-0"
                      title="Ingresar zoom manual (50-300)"
                    />
                    <span className="text-sm text-gray-600">%</span>
                  </div>
                  <button
                    onClick={handleZoomIn}
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Acercar"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                  <button
                    onClick={resetZoom}
                    className="p-1 hover:bg-gray-100 rounded text-xs"
                    title="Resetear zoom"
                  >
                    Reset
                  </button>
                </div>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl p-1 hover:bg-gray-100 rounded"
                  title="Cerrar"
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Contenedor de imagen con scroll */}
            <div 
              className="relative overflow-auto max-h-[80vh] bg-gray-100"
              onWheel={handleWheel}
              style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
            >
              <div className="flex items-center justify-center min-h-[400px] p-4">
                <img
                  ref={imageRef}
                  src={selectedImage.full_image_url || selectedImage.image_url}
                  alt={selectedImage.alt_text}
                  className="max-w-full max-h-full object-contain transition-transform duration-200"
                  style={{
                    transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                    transformOrigin: 'center center'
                  }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement
                    target.src = 'https://via.placeholder.com/600x800/CCCCCC/666666?text=Imagen+no+disponible'
                  }}
                  draggable={false}
                />
              </div>
            </div>
            
            {/* Footer con instrucciones */}
            <div className="p-3 bg-gray-50 border-t text-center">
              <p className="text-sm text-gray-600">
                {zoom > 1 ? 'Arrastra para mover • ' : ''}
                Rueda del mouse para zoom • 
                Botones +/- para zoom • 
                Click en Reset para volver al tamaño original
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CollectionGallery
